import { FunctionComponent, useEffect, useState } from "react";
import { useAppSelector } from "./store/hooks";
import {
  doc,
  setDoc,
  onSnapshot,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
  limit,
  deleteDoc,
} from "firebase/firestore";
import { getDB } from "./Firebase";
import { UserProfile, getRankLabel } from "./services/FirebaseUsers";

type FriendsPanelProps = {
  onClose: () => void;
  onInvite: (roomCode: string) => void;
};

interface FriendEntry {
  uid: string;
  nickname: string;
  rating: number;
  addedAt: any;
  status: "accepted" | "pending_sent" | "pending_received";
}

const FriendsPanel: FunctionComponent<FriendsPanelProps> = ({
  onClose,
  onInvite,
}) => {
  const myProfile = useAppSelector((state) => state.user.profile);
  const [friends, setFriends] = useState<FriendEntry[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchResult, setSearchResult] = useState<UserProfile | null>(null);
  const [searchError, setSearchError] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (!myProfile) return;
    const db = getDB();

    const unsub1 = onSnapshot(
      collection(db, "users", myProfile.uid, "friends"),
      (snap) => {
        const data: FriendEntry[] = snap.docs.map((d) => d.data() as FriendEntry);
        setFriends(data);
      }
    );

    return () => unsub1();
  }, [myProfile]);

  const handleSearch = async () => {
    if (!searchInput.trim() || !myProfile) return;
    setIsSearching(true);
    setSearchResult(null);
    setSearchError("");

    try {
      const db = getDB();
      const q = query(
        collection(db, "users"),
        where("nickname", "==", searchInput.trim()),
        limit(1)
      );
      const snap = await getDocs(q);
      if (snap.empty) {
        setSearchError("Игрок не найден");
      } else {
        const found = snap.docs[0].data() as UserProfile;
        if (found.uid === myProfile.uid) {
          setSearchError("Нельзя добавить себя");
        } else {
          setSearchResult(found);
        }
      }
    } catch {
      setSearchError("Ошибка поиска");
    } finally {
      setIsSearching(false);
    }
  };

  const sendFriendRequest = async (target: UserProfile) => {
    if (!myProfile || isSending) return;
    setIsSending(true);
    try {
      const db = getDB();
      // Add to my friends list as pending_sent
      await setDoc(doc(db, "users", myProfile.uid, "friends", target.uid), {
        uid: target.uid,
        nickname: target.nickname,
        rating: target.rating,
        status: "pending_sent",
        addedAt: serverTimestamp(),
      });
      // Add to target's friends list as pending_received
      await setDoc(doc(db, "users", target.uid, "friends", myProfile.uid), {
        uid: myProfile.uid,
        nickname: myProfile.nickname,
        rating: myProfile.rating,
        status: "pending_received",
        addedAt: serverTimestamp(),
      });
      setSearchResult(null);
      setSearchInput("");
    } catch {
      setSearchError("Ошибка при отправке запроса");
    } finally {
      setIsSending(false);
    }
  };

  const acceptFriendRequest = async (friend: FriendEntry) => {
    if (!myProfile) return;
    const db = getDB();
    await setDoc(doc(db, "users", myProfile.uid, "friends", friend.uid), {
      ...friend,
      status: "accepted",
    });
    await setDoc(doc(db, "users", friend.uid, "friends", myProfile.uid), {
      uid: myProfile.uid,
      nickname: myProfile.nickname,
      rating: myProfile.rating,
      status: "accepted",
      addedAt: serverTimestamp(),
    });
  };

  const removeFriend = async (friendUid: string) => {
    if (!myProfile) return;
    const db = getDB();
    await deleteDoc(doc(db, "users", myProfile.uid, "friends", friendUid));
    await deleteDoc(doc(db, "users", friendUid, "friends", myProfile.uid));
  };

  const acceptedFriends = friends.filter((f) => f.status === "accepted");
  const pendingReceived = friends.filter(
    (f) => f.status === "pending_received"
  );

  return (
    <div className="Friends-overlay" onClick={onClose}>
      <div className="Friends-modal" onClick={(e) => e.stopPropagation()}>
        <div className="Friends-header">
          <h2 className="Friends-title">Друзья</h2>
          <button className="Friends-close" onClick={onClose}>&#x2715;</button>
        </div>

        {/* Поиск */}
        <div className="Friends-search-section">
          <div className="Friends-search-row">
            <input
              className="Friends-search-input"
              type="text"
              placeholder="Поиск по никнейму"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              autoComplete="off"
            />
            <button
              className="Friends-search-button"
              onClick={handleSearch}
              disabled={isSearching || !searchInput.trim()}
            >
              {isSearching ? <span className="Lobby-spinner small" /> : "Найти"}
            </button>
          </div>
          {searchError && (
            <div className="Friends-search-error">{searchError}</div>
          )}
          {searchResult && (
            <div className="Friends-search-result">
              <div className="Friends-result-info">
                <span className="Friends-result-nick">{searchResult.nickname}</span>
                <span className="Friends-result-rating">
                  {searchResult.rating} · {getRankLabel(searchResult.rating)}
                </span>
              </div>
              <button
                className="Friends-add-button"
                onClick={() => sendFriendRequest(searchResult)}
                disabled={isSending}
              >
                {isSending ? <span className="Lobby-spinner small" /> : "Добавить"}
              </button>
            </div>
          )}
        </div>

        {/* Входящие запросы — всегда показываем секцию */}
        <div className="Friends-section">
          <div className="Friends-section-title">
            Заявки в друзья
            {pendingReceived.length > 0 && (
              <span className="Friends-requests-badge">{pendingReceived.length}</span>
            )}
          </div>
          {pendingReceived.length === 0 ? (
            <div className="Friends-empty">Входящих заявок нет</div>
          ) : (
            pendingReceived.map((f) => (
              <div key={f.uid} className="Friends-row pending">
                <div className="Friends-row-info">
                  <span className="Friends-row-nick">{f.nickname}</span>
                  <span className="Friends-row-rating">{f.rating} · {getRankLabel(f.rating)}</span>
                </div>
                <div className="Friends-row-actions">
                  <button
                    className="Friends-action-button accept"
                    onClick={() => acceptFriendRequest(f)}
                  >
                    Принять
                  </button>
                  <button
                    className="Friends-action-button decline"
                    onClick={() => removeFriend(f.uid)}
                  >
                    Отклонить
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Друзья */}
        <div className="Friends-section">
          <div className="Friends-section-title">
            Друзья ({acceptedFriends.length})
          </div>
          {acceptedFriends.length === 0 ? (
            <div className="Friends-empty">
              Список друзей пуст. Найдите игроков через поиск.
            </div>
          ) : (
            acceptedFriends.map((f) => (
              <div key={f.uid} className="Friends-row">
                <div className="Friends-row-avatar">
                  {f.nickname.charAt(0).toUpperCase()}
                </div>
                <div className="Friends-row-info">
                  <span className="Friends-row-nick">{f.nickname}</span>
                  <span className="Friends-row-rank">
                    {f.rating} · {getRankLabel(f.rating)}
                  </span>
                </div>
                <div className="Friends-row-actions">
                  <button
                    className="Friends-action-button remove"
                    onClick={() => removeFriend(f.uid)}
                    title="Удалить из друзей"
                  >
                    &#x2715;
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default FriendsPanel;
